import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLearnerAuth } from '../context/LearnerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Mic, 
  Video, 
  Brain, 
  Target, 
  CheckCircle, 
  ArrowRight, 
  BookOpen,
  MessageSquare,
  TrendingUp,
  Award,
  Clock,
  Loader2,
  User,
  Mail,
  Phone,
  Lock,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";

export default function LearnCommunicationLanding() {
  const navigate = useNavigate();
  const { isAuthenticated, register, login } = useLearnerAuth();
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    current_role: 'student'
  });
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData);
      toast.success('Welcome! Your 30-day journey begins now.');
      setShowRegister(false);
      navigate('/learn-communication/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      setShowLogin(false);
      navigate('/learn-communication/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/learn-communication/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const steps = [
    { icon: User, title: "Register", desc: "Create your free account" },
    { icon: Target, title: "Daily Task", desc: "Get your personalized task" },
    { icon: Mic, title: "Practice", desc: "Record your speech" },
    { icon: Brain, title: "AI Feedback", desc: "Get instant analysis" },
    { icon: TrendingUp, title: "Improve", desc: "Track your progress" },
  ];

  const features = [
    {
      icon: Mic,
      title: "Speaking Practice",
      desc: "Record audio and video responses to real-world scenarios"
    },
    {
      icon: Brain,
      title: "AI Analysis",
      desc: "Get instant feedback on grammar, pronunciation, fluency & confidence"
    },
    {
      icon: Target,
      title: "Daily Targets",
      desc: "Structured 30-day journey with progressive challenges"
    },
    {
      icon: MessageSquare,
      title: "B2B Focus",
      desc: "Learn professional communication for sales & business roles"
    },
    {
      icon: Video,
      title: "Video Recording",
      desc: "Practice visual communication from Day 15"
    },
    {
      icon: Award,
      title: "Completion Badge",
      desc: "Earn recognition upon completing all 30 days"
    }
  ];

  const phases = [
    { days: "1-5", title: "Grammar & Clarity", color: "bg-blue-500" },
    { days: "6-10", title: "Intonation & Voice", color: "bg-purple-500" },
    { days: "11-15", title: "Pronunciation & MTI", color: "bg-pink-500" },
    { days: "16-20", title: "Confidence & Structure", color: "bg-orange-500" },
    { days: "21-25", title: "B2B Communication", color: "bg-green-500" },
    { days: "26-30", title: "Simulation & Assessment", color: "bg-red-500" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={HFS_LOGO} alt="HFS" className="h-10 w-10" />
              <div>
                <span className="font-bold text-hfs-navy">Hire For Skillz</span>
                <span className="hidden sm:inline text-slate-400 mx-2">|</span>
                <span className="hidden sm:inline text-slate-600">Learn Communication</span>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => setShowLogin(true)}
                data-testid="learn-login-btn"
              >
                Login
              </Button>
              <Button 
                onClick={() => setShowRegister(true)}
                className="bg-hfs-navy hover:bg-hfs-navy-dark"
                data-testid="learn-register-btn"
              >
                Start Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="h-4 w-4" />
            100% FREE • No Payment Required
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-hfs-navy leading-tight mb-6">
            Improve Your English Communication with{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Real Practice, Real Feedback
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            AI-powered daily tasks, speaking practice, and real-time feedback — 
            designed to make you interview-ready in 30 days.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => setShowRegister(true)}
              className="bg-hfs-navy hover:bg-hfs-navy-dark text-white px-8 h-14 text-lg"
              data-testid="hero-start-btn"
            >
              Start Free Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowLogin(true)}
              className="px-8 h-14 text-lg"
            >
              Already Registered? Login
            </Button>
          </div>
        </div>
      </section>

      {/* What This Is Section */}
      <section className="py-16 bg-hfs-navy text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            What You'll Get
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-hfs-gold mb-2">30</div>
              <div className="text-slate-300">Day Journey</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-hfs-gold mb-2">AI</div>
              <div className="text-slate-300">Powered Feedback</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-hfs-gold mb-2">4</div>
              <div className="text-slate-300">Skill Metrics</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-hfs-gold mb-2">∞</div>
              <div className="text-slate-300">Practice Sessions</div>
            </div>
          </div>
          
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-white/10 rounded-xl p-6">
              <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-3" />
              <p>Grammar, Pronunciation, Fluency, Confidence scores</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <Clock className="h-8 w-8 text-blue-400 mx-auto mb-3" />
              <p>Daily targets + structured practice (20-40 mins/day)</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <Award className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
              <p>Badge only after completing all 30 days</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-hfs-navy mb-4">
            How It Works
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            A simple 5-step process repeated daily for consistent improvement
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 md:gap-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center text-center w-32 md:w-40">
                  <div className="w-16 h-16 rounded-full bg-hfs-navy text-white flex items-center justify-center mb-3">
                    <step.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-semibold text-hfs-navy">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="hidden md:block h-6 w-6 text-slate-300 mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 30-Day Journey Phases */}
      <section className="py-16 bg-slate-50 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-hfs-navy mb-4">
            Your 30-Day Journey
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            AI dynamically adjusts difficulty based on your performance
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {phases.map((phase, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className={`${phase.color} text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3`}>
                  Days {phase.days}
                </div>
                <h3 className="font-semibold text-hfs-navy text-sm">{phase.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-hfs-navy mb-12">
            Platform Features
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:border-hfs-navy/20 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-hfs-navy/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-hfs-navy" />
                </div>
                <h3 className="font-semibold text-hfs-navy text-lg mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-hfs-navy to-blue-800 text-white px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Communication?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Join hundreds of learners improving their English communication skills daily.
          </p>
          <Button 
            size="lg"
            onClick={() => setShowRegister(true)}
            className="bg-hfs-gold hover:bg-hfs-gold-dark text-hfs-navy px-10 h-14 text-lg font-semibold"
          >
            Start Your Free 30-Day Journey
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-slate-400 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <img src={HFS_LOGO} alt="HFS" className="h-8 w-8" />
          <span className="text-white font-semibold">Hire For Skillz</span>
        </div>
        <p className="text-sm">© 2024 Hire For Skillz. Learn Communication Platform.</p>
        <p className="text-xs mt-2">This is a practice + feedback + correction system. Success = You speak better on Day 30 than Day 1.</p>
      </footer>

      {/* Register Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-hfs-navy">Start Your Free Journey</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                  placeholder="Your full name"
                  className="pl-10"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  placeholder="your@email.com"
                  className="pl-10"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="mobile"
                  value={registerData.mobile}
                  onChange={(e) => setRegisterData({...registerData, mobile: e.target.value})}
                  placeholder="+91 9876543210"
                  className="pl-10"
                  required
                  data-testid="register-mobile"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  placeholder="Create a password"
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Current Status</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <select
                  id="role"
                  value={registerData.current_role}
                  onChange={(e) => setRegisterData({...registerData, current_role: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border rounded-md bg-white text-slate-900"
                  data-testid="register-role"
                >
                  <option value="student">Student</option>
                  <option value="working_professional">Working Professional</option>
                  <option value="fresher">Fresher</option>
                  <option value="career_change">Career Change</option>
                </select>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-hfs-navy hover:bg-hfs-navy-dark h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  Start 30-Day Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            
            <p className="text-center text-sm text-slate-500">
              Already have an account?{' '}
              <button 
                type="button"
                onClick={() => { setShowRegister(false); setShowLogin(true); }}
                className="text-hfs-navy font-medium hover:underline"
              >
                Login
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-hfs-navy">Welcome Back</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  placeholder="your@email.com"
                  className="pl-10"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  placeholder="Your password"
                  className="pl-10"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-hfs-navy hover:bg-hfs-navy-dark h-12"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Continue Learning'
              )}
            </Button>
            
            <p className="text-center text-sm text-slate-500">
              Don't have an account?{' '}
              <button 
                type="button"
                onClick={() => { setShowLogin(false); setShowRegister(true); }}
                className="text-hfs-navy font-medium hover:underline"
              >
                Register Free
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
